import { useCallback, useMemo } from 'react';

import qs from 'qs';

import { useTracking } from '@change-corgi/core/react/tracking';

import { usePsfPromoteOrSharePageData } from 'src/app/pages/psf/promoteOrShare/pageContext';
import { useEncodedBanditDataForUrl, usePageCopy } from 'src/app/pages/psf/promoteOrShare/shared/hooks';
import { useQueryParamValue } from 'src/app/shared/hooks/location';

type Result = ModelHookResult<
	{
		skipLabel: string;
		skipUrl: string;
	},
	{
		handleSkipClick: () => void;
	}
>;

export function usePageFooter(): Result {
	const allowActions = useQueryParamValue('allow_actions');
	const sourceLocation = useQueryParamValue('source_location');
	const guest = useQueryParamValue('guest');
	const shortDisplayName = useQueryParamValue('short_display_name');
	const {
		data: {
			petition: { slug },
		},
	} = usePsfPromoteOrSharePageData();
	const {
		data: { skipLinkText },
	} = usePageCopy();
	const {
		data: { query: banditQueryParams },
	} = useEncodedBanditDataForUrl();
	const track = useTracking();

	const skipUrl = useMemo(() => {
		if (allowActions === 'true') {
			return `/p/${encodeURIComponent(slug)}/actions?source_location=${sourceLocation || 'combo_psf'}`;
		}

		return `/p/${encodeURIComponent(slug)}/psf/share${qs.stringify(
			{
				skip: 1,
				source_location: sourceLocation || 'combo_psf',
				psf_variant: 'combo',
				...(['new', 'existing'].includes(guest || '') ? { from_guest_psf: '1' } : undefined),
				...(shortDisplayName ? { short_display_name: shortDisplayName } : undefined),
				...(allowActions ? { allow_actions: allowActions } : undefined),
				...banditQueryParams,
			},
			{ addQueryPrefix: true },
		)}`;
	}, [slug, allowActions, sourceLocation, guest, shortDisplayName, banditQueryParams]);

	return {
		data: {
			skipLabel: skipLinkText,
			skipUrl,
		},
		actions: {
			handleSkipClick: useCallback(() => {
				void track('combo_psf_skip', { context: 'psf_combo_promote_or_share' });
			}, [track]),
		},
	};
}
